import type { UserDataLoginRecord } from '@navio/nsdk';
import { captureException, captureMessage, setUser } from '@sentry/react';
import { castErrorMessageToString, ErrorWithCause, getApiErrorFingerprint, LogInput, logToConsole } from 'noddi-util';

const isProd = import.meta.env.MODE === 'production';

function logToSentry({ value, severity, cause, extra }: LogInput) {
  if (severity === 'debug') {
    return;
  }

  // eslint-disable-next-line no-restricted-syntax -- prefixed with method and URL for network errors
  let errorMessage = castErrorMessageToString(value);

  // Enhance error message for network errors
  if (extra?.requestUrl) {
    const method = extra.requestMethod || 'unknown';
    const url = extra.requestUrl;
    errorMessage = `${method.toUpperCase()} ${url} failed: ${errorMessage}`;
  }

  if (cause instanceof Error) {
    captureException(new ErrorWithCause(errorMessage, cause), {
      fingerprint: getApiErrorFingerprint(cause),
      level: severity,
      extra
    });
  } else if (value instanceof Error) {
    captureException(value, {
      fingerprint: getApiErrorFingerprint(value),
      level: severity,
      extra
    });
  } else {
    // `captureException` with a string has no real stacktrace, so Sentry
    // groups every string log under the synthetic stack ending at this
    // function — i.e. every `log({ value: '...' })` collapses into one
    // mega-issue titled "logToSentry". `captureMessage` groups by the
    // message text instead, which is what we want here.
    captureMessage(errorMessage, {
      level: severity,
      extra
    });
  }
}

function log({ value, severity, cause, extra }: LogInput) {
  if (isProd) {
    logToSentry({ value, severity, cause, extra });
  } else {
    logToConsole({ value, severity, cause });
  }
}

function setSentryUser({ user }: { user: UserDataLoginRecord }) {
  if (!isProd) {
    return;
  }

  setUser({ id: user.id.toString(), email: user.email ?? undefined });
}

export { log, setSentryUser };
